import React, { useCallback, useEffect, useState } from 'react';
import {
  Box, Typography, Paper, Stack, Button, CircularProgress, TextField, InputAdornment,
  ToggleButtonGroup, ToggleButton,
} from '@mui/material';
import DomainIcon from '@mui/icons-material/Domain';
import SearchIcon from '@mui/icons-material/Search';
import { useNavigate } from 'react-router-dom';
import { listAllClinics, switchToClinic, updateClinicPlan } from '../services/api';
import { useAuth } from '../hooks/useAuth';
import { useNotification } from '../hooks/useNotification';

// "Modo plataforma" — lo que ve un superadmin sin ninguna clínica activa
// todavía (homeRoute en Layout.js apunta acá). No tiene sentido mostrarle
// el dashboard normal, que asume una clínica elegida. El selector del
// AppBar (ClinicPicker en Layout.js) hace lo mismo desde cualquier
// pantalla — esto es la vista completa, con más contexto por clínica.
const SuperadminHome = () => {
  const { reload } = useAuth();
  const notify = useNotification();
  const navigate = useNavigate();
  const [clinics, setClinics] = useState([]);
  const [loading, setLoading] = useState(true);
  const [switchingId, setSwitchingId] = useState(null);
  const [search, setSearch] = useState('');
  const [changingPlanId, setChangingPlanId] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    listAllClinics().then(r => setClinics(r.data || [])).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const handlePlanChange = async (clinicId, plan) => {
    if (!plan) return;
    setChangingPlanId(clinicId);
    try {
      await updateClinicPlan(clinicId, plan);
      setClinics(prev => prev.map(c => c.id === clinicId ? { ...c, plan } : c));
      notify(`Plan actualizado a ${plan === 'pro' ? 'Pro' : 'Free'}`, 'success');
    } catch (err) {
      notify(err.userMessage || 'No se pudo cambiar el plan', 'error');
    } finally {
      setChangingPlanId(null);
    }
  };

  useEffect(() => { load(); }, [load]);

  const handleEnter = async (clinicId) => {
    setSwitchingId(clinicId);
    try {
      const res = await switchToClinic(clinicId);
      localStorage.setItem('token', res.data.access_token);
      await reload();
      // Tras el switch, role pasa de "superadmin" a "admin" — la ruta
      // /superadmin (requiredRoles=['superadmin']) ya no es accesible,
      // hay que navegar explícitamente a la home de un admin normal.
      navigate('/metrics', { replace: true });
    } catch (err) {
      notify(err.userMessage || 'No se pudo entrar a esa clínica', 'error');
    } finally {
      setSwitchingId(null);
    }
  };

  const filtered = search.trim()
    ? clinics.filter(c => c.name.toLowerCase().includes(search.trim().toLowerCase()))
    : clinics;

  return (
    <Box>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" gutterBottom>Clínicas</Typography>
        <Typography variant="body2" color="text.secondary">
          Elegí una clínica para entrar con control total, como soporte. Cada acción queda
          marcada en la auditoría de esa clínica.
        </Typography>
      </Box>

      <TextField
        placeholder="Buscar clínica…"
        value={search}
        onChange={e => setSearch(e.target.value)}
        size="small"
        sx={{ mb: 2, minWidth: 280 }}
        InputProps={{
          startAdornment: <InputAdornment position="start"><SearchIcon sx={{ color: 'text.disabled', fontSize: 20 }} /></InputAdornment>,
        }}
      />

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>
      ) : filtered.length === 0 ? (
        <Paper sx={{ py: 6, textAlign: 'center' }}>
          <Typography color="text.secondary">Sin clínicas{search ? ` que coincidan con "${search}"` : ' registradas'}.</Typography>
        </Paper>
      ) : (
        <Stack spacing={1.5}>
          {filtered.map(c => (
            <Paper key={c.id} variant="outlined" sx={{
              p: 2, display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap',
            }}>
              <DomainIcon sx={{ color: 'text.disabled' }} />
              <Box sx={{ flex: 1, minWidth: 200 }}>
                <Typography fontWeight={700}>{c.name}</Typography>
                <Typography variant="caption" color="text.secondary">
                  {c.active_users} usuario{c.active_users === 1 ? '' : 's'} activo{c.active_users === 1 ? '' : 's'}
                  {' · '}{c.active_patients} paciente{c.active_patients === 1 ? '' : 's'}
                  {c.last_activity ? ` · última actividad ${new Date(c.last_activity + 'Z').toLocaleDateString('es-AR')}` : ''}
                </Typography>
              </Box>
              <ToggleButtonGroup
                value={c.plan || 'free'}
                exclusive
                size="small"
                onChange={(_, plan) => handlePlanChange(c.id, plan)}
                disabled={changingPlanId === c.id}
              >
                <ToggleButton value="free">Free</ToggleButton>
                <ToggleButton value="pro">Pro</ToggleButton>
              </ToggleButtonGroup>
              <Button
                variant="contained" size="small"
                onClick={() => handleEnter(c.id)}
                disabled={switchingId === c.id}
              >
                {switchingId === c.id ? <CircularProgress size={16} /> : 'Entrar'}
              </Button>
            </Paper>
          ))}
        </Stack>
      )}
    </Box>
  );
};

export default SuperadminHome;
